import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import usePollAnalysisJobs from '@/features/analysis/hooks/usePollAnalysisJobs';
import useAnalyzeEmail from '@/features/analysis/mutations/useAnalyzeEmail';
import useTriageEmail from '@/features/analysis/mutations/useTriageEmail';
import useUploadEml from '@/features/analysis/mutations/useUploadEml';
import useAnalyzedEmail from '@/features/analysis/queries/hooks/useAnalyzedEmail';
import useCreateDocumentForDistribution from '@/features/documents/mutations/useCreateDocumentForDistribution';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import OfficeAsyncError from '@/features/email/outlook/OfficeAsyncError';
import type { EmailMessage } from '@/features/email/types';
import type { PartyData } from '@/features/party/types';
import { buildEmlFilename } from '@/lib/email/buildEmlFilename';
import queryKeys from '@/queries/queryKeys';

export default function useAnalyzeFlow(
  email: Maybe<EmailMessage>,
  party: PartyData,
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { getEmlBlob } = useEmailClient();

  const [isReAnalyzing, setIsReAnalyzing] = useState(false);
  const [hasAnalysisFailed, setHasAnalysisFailed] = useState(false);
  const lastAnalyzedKey = useRef<string | null>(null);
  const [pendingJobs, setPendingJobs] = useState<{
    entitiesJobId: string;
    emailId: string;
  } | null>(null);

  const createDocumentForDistributionMutation =
    useCreateDocumentForDistribution();
  const uploadEmlMutation = useUploadEml();
  const triageEmailMutation = useTriageEmail();
  const analyzeEmailMutation = useAnalyzeEmail();

  const { data: analyzedEmail } = useAnalyzedEmail(email?.messageId ?? '', {
    enabled: Boolean(email?.messageId),
  });

  const clearPendingJobs = useCallback(() => setPendingJobs(null), []);

  const { isPolling } = usePollAnalysisJobs({
    entitiesJobId: pendingJobs?.entitiesJobId,
    emailId: pendingJobs?.emailId,
    messageId: email?.messageId,
    onComplete: clearPendingJobs,
  });

  const isPollingAnalysis = isPolling || pendingJobs !== null;

  const isAnalyzing =
    createDocumentForDistributionMutation.isPending ||
    uploadEmlMutation.isPending ||
    triageEmailMutation.isPending ||
    analyzeEmailMutation.isPending;

  const createDocument = async () => {
    if (!email)
      throw new Error('No email data when trying to create document for party');

    const filename = buildEmlFilename(email?.subject);
    const description = email?.messageId;
    const tags = ['MAIL'];

    const result = await createDocumentForDistributionMutation.mutateAsync({
      filename,
      description,
      tags,
      isHiddenForCustomer: true,
      type: 'OTHER',
    });

    return {
      documentId: result.document_id,
      uploadLink: result.link,
      publicUrl: result.public_url,
    };
  };

  const uploadEml = async (uploadLink: string) => {
    const eml = await getEmlBlob();

    await uploadEmlMutation.mutateAsync({ link: uploadLink, blob: eml });
  };

  const triageEmail = async ({
    documentId,
    publicUrl,
  }: {
    documentId: string;
    publicUrl: string;
  }) => {
    if (!email) throw new Error('No email found when trying to triage');

    const result = await triageEmailMutation.mutateAsync({
      documentId,
      link: publicUrl,
    });

    queryClient.invalidateQueries({
      queryKey: queryKeys.emailAnalysis.byMessageId(email.messageId),
    });

    return result;
  };

  const prepareEmailForAnalysis = async () => {
    // Email has already been prepared
    if (analyzedEmail?.email.id) {
      return {
        emailId: analyzedEmail.email.id,
        classification: analyzedEmail.email.triage_classification,
      };
    }

    const { documentId, uploadLink, publicUrl } = await createDocument();

    await uploadEml(uploadLink);

    const triage = await triageEmail({ documentId, publicUrl });

    return {
      emailId: triage.email_id,
      classification: triage.triage_classification,
    };
  };

  const analyzeEmail = async (options?: { forceAnalyze?: boolean }) => {
    const startedAt = performance.now();

    if (!email) return;
    const partyId = party.party?.id;
    if (!partyId) return;

    const key = `${email.messageId}:${partyId}`;
    if (!options?.forceAnalyze && lastAnalyzedKey.current === key) return;
    lastAnalyzedKey.current = key;

    setHasAnalysisFailed(false);

    try {
      posthog.capture('Started email analysis', {
        messageId: email.messageId,
        partyId,
      });

      const triage = await prepareEmailForAnalysis();

      const shouldAnalyze =
        triage.classification === 'INSURANCE_RELATED' || options?.forceAnalyze;
      if (!shouldAnalyze) {
        posthog.capture('Completed email analysis', {
          messageId: email.messageId,
          partyId,
          emailId: triage.emailId,
          classification: triage.classification,
          analyzed: false,
          durationMs: Math.round(performance.now() - startedAt),
        });

        return;
      }

      const analysis = await analyzeEmailMutation.mutateAsync({
        emailId: triage.emailId,
        partyId,
      });

      if (analysis.entitiesJobId) {
        setPendingJobs({
          entitiesJobId: analysis.entitiesJobId,
          emailId: analysis.emailId,
        });
      }

      posthog.capture('Completed email analysis', {
        messageId: email.messageId,
        partyId,
        emailId: triage.emailId,
        classification: triage.classification,
        analyzed: true,
        durationMs: Math.round(performance.now() - startedAt),
      });
    } catch (error) {
      setHasAnalysisFailed(true);
      toast.error(t('EMAIL_ANALYSIS_FAILED'));

      posthog.capture('Email analysis failed', {
        isFailure: true,
        messageId: email.messageId,
        partyId,
        durationMs: Math.round(performance.now() - startedAt),
        errorMessage: error instanceof Error ? error.message : String(error),
        ...(error instanceof OfficeAsyncError && {
          officeErrorCode: error.officeErrorCode,
          officeErrorName: error.officeErrorName,
        }),
      });
      Sentry.captureException(error);
    }
  };

  const reAnalyze = async () => {
    if (!email) return;

    setIsReAnalyzing(true);
    await analyzeEmail({ forceAnalyze: true });
    setIsReAnalyzing(false);
  };

  return {
    isAnalyzing,
    isReAnalyzing,
    isPollingAnalysis,
    hasAnalysisFailed,
    analyzeEmail,
    reAnalyze,
  };
}
